// src/pages/AnalysePage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import styles from './AnalysePage.module.css';

import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  X,
  Sparkles,
  Clock,
  RotateCcw,
  ChevronRight,
  Zap,
  Shield,
  BarChart3,
  Loader2,
  Mail
} from 'lucide-react';

import AnalyseResult from './AnalyseResult';

const POLLING_TIMEOUT_MS = 10 * 60 * 1000;

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS_STRING = '.pdf,.doc,.docx';

// ── Les étapes réelles du backend d'analyse ──
const MAIN_STEPS = [
  { key: 'downloading', text: "Téléchargement sécurisé du CV..." },
  { key: 'extracting',  text: "Extraction du texte et traitement OCR..." },
  { key: 'ai_analysis', text: "Audit IA et normalisation ESCO..." },
  { key: 'vectorizing', text: "Calcul de la cohérence sémantique..." },
  { key: 'market_analysis', text: "Récupération des données marché (France Travail)..." }
];

// ── Les sous-étapes simulées pour faire patienter pendant 'ai_analysis' ──
const AI_STEPS = [
  "Identification du rôle et de la séniorité...",
  "Normalisation du profil selon le standard européen ESCO...",
  "Évaluation de l'impact des réalisations (KPIs, ROI)...",
  "Audit de la stack technique et détection d'obsolescence...",
  "Analyse des soft skills et du potentiel de leadership...",
  "Identification des compétences manquantes...",
  "Génération de recommandations d'optimisation stratégique..."
];

// ─── Carte feature ───────────────────────────────────────────
const FeatureCard = ({ icon: Icon, title, desc, color }) => (
  <div className={styles.featureCard}>
    <span className={styles.featureIcon} style={{ '--feature-color': color }}>
      <Icon size={18} strokeWidth={1.75} />
    </span>
    <div>
      <p className={styles.featureTitle}>{title}</p>
      <p className={styles.featureDesc}>{desc}</p>
    </div>
  </div>
);

// ─── Step indicator ──────────────────────────────────────────
const StepDot = ({ step, current, label }) => (
  <div className={`${styles.stepDot} ${current >= step ? styles.stepDotActive : ''}`}>
    <span className={styles.stepNum}>{step}</span>
    <span className={styles.stepLabel}>{label}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const AnalysePage = () => {
  const { refreshToken, quota } = useAuth();

  const [file1, setFile1] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState('idle');
  const [taskId, setTaskId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultData, setResultData] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  // ── ÉTATS DU CHARGEMENT HYBRIDE (CATCH-UP SYSTEM) ──
  const [backendStep, setBackendStep] = useState('downloading'); 
  const [visualStepIndex, setVisualStepIndex] = useState(0);     
  const [aiSubStep, setAiSubStep] = useState(0);                 

  const pollingInterval = useRef(null);
  const timerInterval = useRef(null);
  const file1InputRef = useRef(null);
  const startTimeRef = useRef(null);
  const dropZoneRef = useRef(null);

  const updateStartTime = useCallback((time) => {
    startTimeRef.current = time;
  }, []);

  const startTimer = useCallback(() => {
    if (timerInterval.current) clearInterval(timerInterval.current);
    timerInterval.current = setInterval(() => {
      if (startTimeRef.current) {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerInterval.current) clearInterval(timerInterval.current);
    setElapsed(0);
  }, []);

  const stopProcessing = useCallback(
    (stopStatus, message = '') => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      stopTimer();
      localStorage.removeItem('analyseTask');
      setStatus(stopStatus);
      if (message) setErrorMessage(message);
      updateStartTime(null);

      // Réinitialisation des étapes hybrides
      setBackendStep('downloading');
      setVisualStepIndex(0);
      setAiSubStep(0);
    },
    [updateStartTime, stopTimer]
  );

  const checkTaskStatus = useCallback(
    async (currentTaskId, isRecovery = false) => {
      if (!currentTaskId) return;
      try {
        const response = await api.get(`/status/${currentTaskId}`);
        const data = response.data;

        if (data.status === 'completed') {
          setResultData(data);
          stopProcessing('completed');
        } else if (data.status === 'error') {
          stopProcessing('error', data.message || "Une erreur est survenue durant l'analyse.");
        } else if (data.status === 'processing') {
          // MISE À JOUR DE LA RÉALITÉ DU BACKEND
          if (data.progress_step) {
            setBackendStep(data.progress_step);
          }
        } else {
          const startTime = startTimeRef.current;
          if (startTime && Date.now() - startTime > POLLING_TIMEOUT_MS) {
            stopProcessing('error', 'Le traitement a dépassé 10 minutes et a été arrêté.');
          }
        }
      } catch {
        if (!isRecovery) {
          stopProcessing('error', 'Erreur de connexion lors de la vérification du statut.');
        }
      }
    },
    [stopProcessing]
  );

  const startPolling = useCallback(
    (currentTaskId) => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      checkTaskStatus(currentTaskId, false); // Check immédiat
      pollingInterval.current = setInterval(() => {
        checkTaskStatus(currentTaskId, false);
      }, 10000);
    },
    [checkTaskStatus]
  );

  // ── EFFET 1 : LE RATTRAPAGE VISUEL (CATCH-UP) ──
  useEffect(() => {
    if (status !== 'processing') return;
    const targetIndex = MAIN_STEPS.findIndex(s => s.key === backendStep);
    
    if (visualStepIndex < targetIndex) {
      const timer = setTimeout(() => {
        setVisualStepIndex(prev => prev + 1);
      }, 1500); // 1.5s par étape rattrapée
      return () => clearTimeout(timer);
    }
  }, [status, backendStep, visualStepIndex]);

  // ── EFFET 2 : L'ANIMATION PENDANT L'IA ──
  useEffect(() => {
    if (status !== 'processing') return;

    if (MAIN_STEPS[visualStepIndex]?.key === 'ai_analysis') {
      const timer = setInterval(() => {
        setAiSubStep((prev) => (prev < AI_STEPS.length - 1 ? prev + 1 : prev));
      }, 8000); // L'audit est un peu plus rapide, on défile toutes les 8s
      return () => clearInterval(timer);
    }
  }, [status, visualStepIndex]);

  // Restauration de session
  useEffect(() => {
    const savedTaskJSON = localStorage.getItem('analyseTask');
    if (savedTaskJSON) {
      try {
        const savedTask = JSON.parse(savedTaskJSON);
        if (savedTask?.id && savedTask.status === 'processing') {
          setTaskId(savedTask.id);
          setStatus('processing');
          if (savedTask.startTime) {
            updateStartTime(savedTask.startTime);
            startTimer();
          }
          if (savedTask.fileName1) setFile1({ name: savedTask.fileName1 });

          checkTaskStatus(savedTask.id, true).then(() => {
            const st = startTimeRef.current;
            if (!st || Date.now() - st <= POLLING_TIMEOUT_MS) {
              startPolling(savedTask.id);
            }
          });
        } else {
          localStorage.removeItem('analyseTask');
        }
      } catch {
        localStorage.removeItem('analyseTask');
      }
    }
  }, [checkTaskStatus, startPolling, startTimer, updateStartTime]);

  // ── Visibility change ────────────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'processing' && taskId) {
        checkTaskStatus(taskId, false);
        startPolling(taskId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      // ✅ ON NE TUE PLUS LE POLLING ICI. 
      // On se contente juste de nettoyer l'écouteur d'événement.
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, taskId, checkTaskStatus, startPolling]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { if (!dropZoneRef.current?.contains(e.relatedTarget)) setIsDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); const dropped = e.dataTransfer.files[0]; if (dropped) handleFile1Change(dropped); };

  const handleFile1Change = (selectedFile) => {
    if (selectedFile && ALLOWED_MIME_TYPES.includes(selectedFile.type)) {
      setFile1(selectedFile);
      setErrorMessage('');
    } else {
      setFile1(null);
      setErrorMessage('Fichier invalide. Seuls les fichiers PDF et Word (.pdf, .doc, .docx) sont autorisés.');
      if (file1InputRef.current) file1InputRef.current.value = null;
    }
  };

  const removeFile1 = (e) => {
    e.stopPropagation();
    setFile1(null);
    if (file1InputRef.current) file1InputRef.current.value = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file1) { setErrorMessage('Veuillez sélectionner un fichier CV.'); return; }

    // Reset états hybrides
    setStatus('processing');
    setBackendStep('downloading');
    setVisualStepIndex(0);
    setAiSubStep(0);
    setErrorMessage('');

    const formData = new FormData();
    formData.append('file1', file1);

    try {
      const response = await api.post('/analyse/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { task_id, new_token } = response.data;
      if (new_token) refreshToken(new_token);

      setTaskId(task_id);
      const startTime = Date.now();
      updateStartTime(startTime);
      startTimer();

      localStorage.setItem(
        'analyseTask',
        JSON.stringify({ id: task_id, status: 'processing', fileName1: file1.name, startTime })
      );
      startPolling(task_id);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.error || "Erreur lors de l'envoi du fichier.");
    }
  };

  const resetState = () => {
    setFile1(null);
    if (file1InputRef.current) file1InputRef.current.value = null;
    stopProcessing('idle');
    setTaskId(null);
    setResultData(null);
  };

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, '0')}s` : `${sec}s`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderInner}>
          <span className={styles.pageBadge}>
            <Zap size={12} strokeWidth={2.5} /> Intelligence artificielle
          </span>
          <h1 className={styles.pageTitle}>Audit de CV</h1>
          <p className={styles.pageSubtitle}>
            Analysez la cohérence et la qualité d'un CV en quelques secondes grâce à notre IA.
          </p>
        </div>
        {quota !== null && quota !== undefined && (
          <div className={styles.quotaBadge}>
            <Shield size={14} strokeWidth={2} className={styles.quotaIcon} />
            <span>Crédits disponibles</span>
            <strong className={styles.quotaValue}>{quota}</strong>
          </div>
        )}
      </div>

      <div className={styles.stepper}>
        <StepDot step={1} current={status === 'idle' ? 1 : 2} label="Upload" />
        <div className={`${styles.stepLine} ${status !== 'idle' ? styles.stepLineActive : ''}`} />
        <StepDot step={2} current={status === 'processing' ? 2 : status !== 'idle' ? 3 : 1} label="Analyse" />
        <div className={`${styles.stepLine} ${status === 'completed' || status === 'error' ? styles.stepLineActive : ''}`} />
        <StepDot step={3} current={status === 'completed' || status === 'error' ? 3 : 1} label="Résultat" />
      </div>

      {status === 'idle' && (
        <div className={styles.contentGrid}>
          {/* Colonne principale - Le Formulaire reste identique */}
          <div className={styles.mainCol}>
            <form onSubmit={handleSubmit} className={styles.card}>
              <div className={styles.cardHeader}>
                <FileText size={20} strokeWidth={1.75} className={styles.cardHeaderIcon} />
                <div>
                  <h2 className={styles.cardTitle}>Sélectionner un CV</h2>
                  <p className={styles.cardSub}>Format PDF, DOC ou DOCX — 10 Mo max</p>
                </div>
              </div>

              <div
                ref={dropZoneRef}
                className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''} ${file1 ? styles.dropZoneHasFile : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={!file1 ? () => file1InputRef.current?.click() : undefined}
              >
                <input type="file" id="file1" hidden onChange={(e) => handleFile1Change(e.target.files[0])} ref={file1InputRef} accept={ALLOWED_EXTENSIONS_STRING} />
                {file1 ? (
                  <div className={styles.filePreview}>
                    <div className={styles.filePreviewIcon}><FileText size={28} strokeWidth={1.5} /></div>
                    <div className={styles.filePreviewInfo}>
                      <span className={styles.filePreviewName}>{file1.name}</span>
                      {file1.size && <span className={styles.filePreviewSize}>{formatSize(file1.size)}</span>}
                    </div>
                    <button type="button" onClick={removeFile1} className={styles.removeBtn}><X size={16} strokeWidth={2.5} /></button>
                  </div>
                ) : (
                  <div className={styles.dropZoneContent}>
                    <div className={styles.uploadIconWrap}><Upload size={28} strokeWidth={1.5} /></div>
                    <p className={styles.dropZoneTitle}>{isDragging ? 'Déposez le fichier ici' : 'Glissez votre CV ici'}</p>
                    <p className={styles.dropZoneSub}>ou <span className={styles.browseLink}>parcourir vos fichiers</span></p>
                    <div className={styles.dropZoneFormats}><span>PDF</span><span>DOC</span><span>DOCX</span></div>
                  </div>
                )}
              </div>
              {errorMessage && (
                <div className={styles.alertError}><AlertTriangle size={16} strokeWidth={2} /><span>{errorMessage}</span></div>
              )}
              <div className={styles.formFooter}>
                <button type="submit" className={styles.btnPrimary} disabled={!file1}>
                  <Sparkles size={16} strokeWidth={2} /> Lancer l'analyse <ChevronRight size={16} strokeWidth={2.5} />
                </button>
              </div>
            </form>
          </div>

          <div className={styles.sideCol}>
            <p className={styles.sideTitle}>Ce que l'IA analyse</p>
            <div className={styles.featuresStack}>
              <FeatureCard icon={BarChart3} title="Score global" desc="Évaluation de la qualité et de la complétude du CV" color="var(--color-primary-600)" />
              <FeatureCard icon={Shield} title="Cohérence" desc="Vérification des dates, compétences et expériences" color="var(--color-success)" />
              <FeatureCard icon={Sparkles} title="Recommandations" desc="Suggestions concrètes pour améliorer le document" color="var(--color-accent)" />
              <FeatureCard icon={Zap} title="Analyse rapide" desc="Résultats disponibles en moins d'une minute" color="var(--color-info)" />
            </div>
          </div>
        </div>
      )}

      {/* ══════════ PROCESSING (HYBRID SKELETON) ══════════ */}
      {status === 'processing' && (
        <div className={styles.skeletonSection}>
          <div className={styles.skeletonLiveHeader}>
            <div className={styles.skeletonLiveIcon}>
              <Loader2 className={styles.spin} size={20} />
            </div>
            <div className={styles.skeletonLiveText}>
              <h4>Audit en cours</h4>
              <p className={styles.skeletonStepMessage}>
                {MAIN_STEPS[visualStepIndex]?.key === 'ai_analysis' 
                  ? AI_STEPS[aiSubStep] 
                  : MAIN_STEPS[visualStepIndex]?.text}
              </p>
            </div>
            {elapsed > 0 && (
              <div className={styles.timerBadgeSkeleton}>
                <Clock size={14} strokeWidth={2} />
                {formatElapsed(elapsed)}
              </div>
            )}
          </div>

          {/* Skeleton adaptatif pour la page d'analyse */}
          <div className={styles.skeletonGrid}>
            <div className={styles.skeletonScoresCol}>
              <div className={`${styles.shimmer} ${styles.skeletonGauge}`} style={{ width: '150px', height: '150px', borderRadius: '50%', margin: '0 auto' }} />
              <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '60%', height: '24px', margin: '24px auto 0' }} />
            </div>
            
            <div className={styles.skeletonSummaryCol}>
              <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '40%', height: '28px', marginBottom: '24px' }} />
              <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '90%', marginBottom: '12px' }} />
              <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '85%', marginBottom: '12px' }} />
              <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '60%', marginBottom: '32px' }} />
              
              <div className={styles.skeletonSwot}>
                <div className={`${styles.shimmer} ${styles.skeletonBox}`} style={{ height: '100px' }} />
                <div className={`${styles.shimmer} ${styles.skeletonBox}`} style={{ height: '100px' }} />
              </div>
            </div>
          </div>
          
          <div className={styles.skeletonPillarsWrapper}>
             <div className={styles.skeletonPillarsGrid}>
               {[1, 2, 3].map(i => (
                 <div key={i} className={`${styles.shimmer} ${styles.skeletonPillarCard}`} style={{ height: '140px' }} />
               ))}
             </div>
          </div>
        </div>
      )}

      {/* ══════════ COMPLETED ══════════ */}
      {status === 'completed' && (
        <div className={styles.completedWrapper}>
          <div className={styles.successBanner}>
            <div className={styles.successBannerIcon}><CheckCircle size={24} strokeWidth={2} /></div>
            <div className={styles.successBannerText}>
              <p className={styles.successBannerTitle}>Analyse terminée avec succès</p>
              <p className={styles.successBannerSub}>Le rapport complet est disponible ci-dessous</p>
            </div>
            <button onClick={resetState} className={styles.btnOutline}><RotateCcw size={14} strokeWidth={2.5} /> Nouveau CV</button>
          </div>
          {resultData && <div className={styles.card}><AnalyseResult data={resultData} /></div>}
        </div>
      )}

      {/* ══════════ ERROR ══════════ */}
      {status === 'error' && (
        <div className={styles.card}>
          <div className={styles.errorWrapper} style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className={styles.errorIconWrap} style={{ display: 'inline-flex', marginBottom: '15px', color: 'var(--color-danger)' }}>
                <AlertTriangle size={42} strokeWidth={1.5} />
            </div>
            <h3 className={styles.errorTitle} style={{ fontSize: '1.25rem', marginBottom: '10px' }}>
                Oups, l'analyse a été interrompue
            </h3>
            
            {/* Le message renvoyé par l'Azure Function s'affichera ici */}
            <p className={styles.errorMessage} style={{ color: 'var(--color-text-muted)', marginBottom: '25px', maxWidth: '500px', margin: '0 auto 25px' }}>
                {errorMessage}
            </p>
            
            <div className={styles.errorActions} style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={resetState} className={styles.btnPrimary}>
                  <RotateCcw size={16} strokeWidth={2} /> Réessayer
              </button>
              
              {/* Ajuste le lien href/to selon ton architecture de routing (ex: Link to="/support") */}
              <a href="/support" className={styles.btnOutline} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Mail size={16} strokeWidth={2} /> Créer un ticket
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysePage;